import React from "react"
import strings from "../../../localization/strings"
import autobind from "class-autobind"
import { patchAccount, patchSpecificAccount } from "../../../actions/AccountActions"
import { IAccountDocument } from "../../../models"
import PunchPreQuestions from "./pre_question/PunchPreQuestions"
import AppStateStore from "../../../stores/AppStateStore"
import _ from "lodash"
import InfoIconToggle from "../../common/InfoIconToggle"

interface IProps {
    account: IAccountDocument
    readOnly?: boolean

    // from Account->manager
    accountManager?: boolean
    updateLoadedAccount?(loadedAccount: IAccountDocument): Promise<IAccountDocument>
}
interface IState {
    enableMobilePhotoNotRequiredOnPunch: boolean
    enableMobilePhotoNotRequiredOnTransfer: boolean
    enableMobileTimeFraudPrevention: boolean
    enableMobileLocationFraudPrevention: boolean
    enableTimeSetEdit: boolean
    enablePunchQuestions: boolean
    autoAddMobileSuspensePunch: boolean
    enableInOutOnMobile: boolean
    enableEmpEditTimecardOnMobile: boolean
    enablePreQuestions: boolean
    missingPunchWorkflow: boolean
    limitDeviceEmployee: boolean
    controlEmpBioSetting: boolean
    presentTimecard: boolean
    confirmMobileNoPeriodically: boolean
    enableLiveness: boolean
    enableGeoFenceTracking: boolean
}

export default class AccountMobileFeatureSettings extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props)
        const featureSettings = props.account.AccountSettings.FeatureSettings
        this.state = {
            enableMobilePhotoNotRequiredOnPunch: featureSettings.EnableMobilePhotoNotRequiredOnPunch,
            enableMobilePhotoNotRequiredOnTransfer: featureSettings.EnableMobilePhotoNotRequiredOnTransfer,
            enableMobileTimeFraudPrevention: featureSettings.EnableMobileTimeFraudPrevention,
            enableMobileLocationFraudPrevention: featureSettings.EnableMobileLocationFraudPrevention,
            enableTimeSetEdit: featureSettings.EnableMobileAllowTimesetEdit,
            enablePunchQuestions: featureSettings.EnablePunchQuestions,
            autoAddMobileSuspensePunch: featureSettings.AutoAddMobileSuspensePunch,
            enableInOutOnMobile: featureSettings.EnableInOutOnMobile,
            enableEmpEditTimecardOnMobile: featureSettings.EnableEmpEditTimecardOnMobile,
            enablePreQuestions: featureSettings.EnablePreQuestions,
            missingPunchWorkflow: featureSettings.MissingPunchWorkflow,
            limitDeviceEmployee: featureSettings.LimitDeviceEmployee,
            controlEmpBioSetting: featureSettings.ControlEmpBioSetting,
            presentTimecard: featureSettings.PresentTimecard,
            confirmMobileNoPeriodically: featureSettings.ConfirmMobileNoPeriodically,
            enableLiveness: featureSettings.EnableLiveness,
            enableGeoFenceTracking: featureSettings.EnableGeoFenceTracking
        }
        autobind(this)
    }

    handleUpdateAccount(account, cb = null) {
        this.props.accountManager ?
            patchSpecificAccount(this.props.account.Id, account, this.props.account.TicksSinceChange,
                res => this.props.updateLoadedAccount(res.data as IAccountDocument).then(() => {
                    cb && cb()
                })
            ) :
            patchAccount(this.props.account.Id, account, cb)
    }

    handleToggleEnableMobilePhotoNotRequiredOnPunch(event, value) {
        AppStateStore.setConfirmDialogContent({
            content: strings.Are_you_sure_you_want_to_change_this_account_setting,
            confirm: () =>
                this.setState({ enableMobilePhotoNotRequiredOnPunch: value }, () =>
                    this.handleUpdateAccount({ AccountSettings: { FeatureSettings: { EnableMobilePhotoNotRequiredOnPunch: value } } })
                )
        })
    }

    handleToggleEnableMobileTimeFraudPrevention(event, value) {
        AppStateStore.setConfirmDialogContent({
            content: strings.Are_you_sure_you_want_to_change_this_account_setting,
            confirm: () =>
                this.setState({ enableMobileTimeFraudPrevention: value }, () =>
                    this.handleUpdateAccount({ AccountSettings: { FeatureSettings: { EnableMobileTimeFraudPrevention: value } } })
                )
        })
    }

    handleToggleEnableMobileLocationFraudPrevention(event, value) {
        AppStateStore.setConfirmDialogContent({
            content: strings.Are_you_sure_you_want_to_change_this_account_setting,
            confirm: () =>
                this.setState({ enableMobileLocationFraudPrevention: value }, () =>
                    this.handleUpdateAccount({ AccountSettings: { FeatureSettings: { EnableMobileLocationFraudPrevention: value } } })
                )
        })
    }

    handleToggleEnableMobilePhotoNotRequiredOnTransfer(event, value) {
        AppStateStore.setConfirmDialogContent({
            content: strings.Are_you_sure_you_want_to_change_this_account_setting,
            confirm: () =>
                this.setState({ enableMobilePhotoNotRequiredOnTransfer: value }, () =>
                    this.handleUpdateAccount({ AccountSettings: { FeatureSettings: { EnableMobilePhotoNotRequiredOnTransfer: value } } })
                )
        })
    }

    handleToggleEnableTimeSetEdit(event, value) {
        AppStateStore.setConfirmDialogContent({
            content: strings.Are_you_sure_you_want_to_change_this_account_setting,
            confirm: () =>
                this.setState({ enableTimeSetEdit: value }, () =>
                    this.handleUpdateAccount({ AccountSettings: { FeatureSettings: { EnableMobileAllowTimesetEdit: value } } })
                )
        })
    }

    handleToggleEnablePunchQuestions(event, value) {
        AppStateStore.setConfirmDialogContent({
            content: strings.Are_you_sure_you_want_to_change_this_account_setting,
            confirm: () =>
                this.setState({ enablePunchQuestions: value }, () =>
                    this.handleUpdateAccount({ AccountSettings: { FeatureSettings: { EnablePunchQuestions: value } } })
                )
        })
    }

    handleToggleEnablePreQuestions(event, value) {
        AppStateStore.setConfirmDialogContent({
            content: strings.Are_you_sure_you_want_to_change_this_account_setting,
            confirm: () =>
                this.setState({ enablePreQuestions: value }, () =>
                    this.handleUpdateAccount({ AccountSettings: { FeatureSettings: { EnablePreQuestions: value } } })
                )
        })
    }

    handleToggleAutoAddMobileSuspensePunch(event, value) {
        AppStateStore.setConfirmDialogContent({
            content: strings.Are_you_sure_you_want_to_change_this_account_setting,
            confirm: () =>
                this.setState({ autoAddMobileSuspensePunch: value }, () =>
                    this.handleUpdateAccount({ AccountSettings: { FeatureSettings: { AutoAddMobileSuspensePunch: value } } })
                )
        })
    }

    handleToggleEnableInOutOnMobile(event, value) {
        AppStateStore.setConfirmDialogContent({
            content: strings.Are_you_sure_you_want_to_change_this_account_setting,
            confirm: () =>
                this.setState({ enableInOutOnMobile: value }, () =>
                    this.handleUpdateAccount({ AccountSettings: { FeatureSettings: { EnableInOutOnMobile: value } } })
                )
        })
    }

    handleToggleEnablePunchDetailEdit(event, value) {
        AppStateStore.setConfirmDialogContent({
            content: strings.Are_you_sure_you_want_to_change_this_account_setting,
            confirm: () =>
                this.setState({ enableEmpEditTimecardOnMobile: value }, () =>
                    this.handleUpdateAccount({ AccountSettings: { FeatureSettings: { EnableEmpEditTimecardOnMobile: value } } })
                )
        })
    }

    handleToggleMissingPunchWorkflow(event, value) {
        AppStateStore.setConfirmDialogContent({
            content: strings.Are_you_sure_you_want_to_change_this_account_setting,
            confirm: () =>
                this.setState({ missingPunchWorkflow: value }, () =>
                    this.handleUpdateAccount({ AccountSettings: { FeatureSettings: { MissingPunchWorkflow: value } } })
                )
        })
    }

    handleToggleLimitDeviceEmployee(event, value) {
        AppStateStore.setConfirmDialogContent({
            content: strings.Are_you_sure_you_want_to_change_this_account_setting,
            confirm: () =>
                this.setState({ limitDeviceEmployee: value }, () =>
                    this.handleUpdateAccount({ AccountSettings: { FeatureSettings: { LimitDeviceEmployee: value } } })
                )
        })
    }

    handleToggleControlEmpBioSetting(event, value) {
        AppStateStore.setConfirmDialogContent({
            content: strings.Are_you_sure_you_want_to_change_this_account_setting,
            confirm: () =>
                this.setState({ controlEmpBioSetting: value }, () =>
                    this.handleUpdateAccount({ AccountSettings: { FeatureSettings: { ControlEmpBioSetting: value } } })
                )
        })
    }

    handleTogglePresentTimecard(event, value) {
        AppStateStore.setConfirmDialogContent({
            content: strings.Are_you_sure_you_want_to_change_this_account_setting,
            confirm: () =>
                this.setState({ presentTimecard: value }, () =>
                    this.handleUpdateAccount({ AccountSettings: { FeatureSettings: { PresentTimecard: value } } })
                )
        })
    }

    handleToggleConfirmMobileNoPeriodically(event, value) {
        AppStateStore.setConfirmDialogContent({
            content: strings.Are_you_sure_you_want_to_change_this_account_setting,
            confirm: () =>
                this.setState({ confirmMobileNoPeriodically: value }, () =>
                    this.handleUpdateAccount({ AccountSettings: { FeatureSettings: { ConfirmMobileNoPeriodically: value } } })
                )
        })
    }

    handleToggleEnableLiveness(event, value) {
        AppStateStore.setConfirmDialogContent({
            content: strings.Are_you_sure_you_want_to_change_this_account_setting,
            confirm: () =>
                this.setState({ enableLiveness: value }, () =>
                    this.handleUpdateAccount({ AccountSettings: { FeatureSettings: { EnableLiveness: value } } })
                )
        })
    }

    
    handleToggleEnableGeoFenceTracking(event, value) {
        AppStateStore.setConfirmDialogContent({
            content: strings.Are_you_sure_you_want_to_change_this_account_setting,
            confirm: () =>
                this.setState({ enableGeoFenceTracking: value }, () => {
                    this.handleUpdateAccount({
                        AccountSettings: { FeatureSettings: { EnableGeoFenceTracking: value } }
                    })
                })
        })
    }
    render() {
        return (
            <div style={this.props.accountManager && { overflow: "auto", height: "calc(100vh - 350px)" }}>
                <InfoIconToggle
                    data-intercom-target={`menu-AccountSettings-Mobile-Toggle-${strings.Do_Not_Require_Photo_On_Punch}`}
                    toggled={this.state.enableMobilePhotoNotRequiredOnPunch}
                    onToggle={this.handleToggleEnableMobilePhotoNotRequiredOnPunch}
                    label={strings.Do_Not_Require_Photo_On_Punch}
                    style={{ maxWidth: 300, marginBottom: 15 }}
                    disabled={this.props.readOnly}
                    info="When employees punch in, the facial recognition screen will not present"
                />
                <InfoIconToggle
                    data-intercom-target={`menu-AccountSettings-Mobile-Toggle-${strings.Do_Not_Require_Photo_On_Transfer}`}
                    toggled={this.state.enableMobilePhotoNotRequiredOnTransfer}
                    onToggle={this.handleToggleEnableMobilePhotoNotRequiredOnTransfer}
                    label={strings.Do_Not_Require_Photo_On_Transfer}
                    style={{ maxWidth: 300, marginBottom: 15 }}
                    disabled={this.props.readOnly}
                    info="When employees transfers from one job/hierarchy to another , the facial recognition screen will not present"
                />
                <InfoIconToggle
                    data-intercom-target={`menu-AccountSettings-Mobile-Toggle-${strings.Enable_Mobile_Time_Fraud_Prevention}`}
                    toggled={this.state.enableMobileTimeFraudPrevention}
                    onToggle={this.handleToggleEnableMobileTimeFraudPrevention}
                    label={strings.Enable_Mobile_Time_Fraud_Prevention}
                    style={{ maxWidth: 300, marginBottom: 15 }}
                    disabled={this.props.readOnly}
                    info="Mobile Fraud Prevention is to catch employees from manually changing a device time or putting on airplane mode on a device to manipulate time when using a mobile clock mode or individual mode device"
                />
                <InfoIconToggle
                    data-intercom-target={`menu-AccountSettings-Mobile-Toggle-${strings.Enable_Mobile_Location_Fraud_Prevention_to_GPS_accuracy}`}
                    toggled={this.state.enableMobileLocationFraudPrevention}
                    onToggle={this.handleToggleEnableMobileLocationFraudPrevention}
                    label={strings.Enable_Mobile_Location_Fraud_Prevention_to_GPS_accuracy}
                    style={{ maxWidth: 300, marginBottom: 15 }}
                    disabled={this.props.readOnly}
                    info="Pop up will be presented telling employee to enable GPS on mobile device if turned off"
                />
                <InfoIconToggle
                    data-intercom-target={`menu-AccountSettings-Mobile-Toggle-${strings.Enable_Date_Time_Edit}`}
                    toggled={this.state.enableTimeSetEdit}
                    onToggle={this.handleToggleEnableTimeSetEdit}
                    label={strings.Enable_Date_Time_Edit}
                    style={{ maxWidth: 300, marginBottom: 15 }}
                    disabled={this.props.readOnly}
                    info="Allows employees to edit or add missing time on their timecard from the mobile device"
                />
                <InfoIconToggle
                    data-intercom-target={`menu-AccountSettings-Mobile-Toggle-${strings.Enable_Punch_Questions}`}
                    toggled={this.state.enablePunchQuestions}
                    onToggle={this.handleToggleEnablePunchQuestions}
                    label={strings.Enable_Punch_Questions}
                    style={{ maxWidth: 300, marginBottom: 15 }}
                    disabled={this.props.readOnly}
                    info="Punch Questions that are set up in Grandd Central will be presented to an employee when punching"
                />
                <InfoIconToggle
                    data-intercom-target={`menu-AccountSettings-Mobile-Toggle-${strings.Automatically_Add_Mobile_Suspense_Punch_to_Timecard}`}
                    toggled={this.state.autoAddMobileSuspensePunch}
                    onToggle={this.handleToggleAutoAddMobileSuspensePunch}
                    label={strings.Automatically_Add_Mobile_Suspense_Punch_to_Timecard}
                    style={{ maxWidth: 300, marginBottom: 15 }}
                    disabled={this.props.readOnly}
                    info="Any suspense punches that an employee has, those punches will automatically show on the timecard withoout approval from managers/supervisors"
                />
                <InfoIconToggle
                    data-intercom-target={`menu-AccountSettings-Mobile-Toggle-${strings.Enable_IN_OUT_on_Mobile}`}
                    toggled={this.state.enableInOutOnMobile}
                    onToggle={this.handleToggleEnableInOutOnMobile}
                    label={strings.Enable_IN_OUT_on_Mobile}
                    style={{ maxWidth: 300, marginBottom: 15 }}
                    disabled={this.props.readOnly}
                    info={`Timecards on mobile will show "In" punches and "Out" punches that will show employees where missing punches are`}
                />
                <InfoIconToggle
                    data-intercom-target={`menu-AccountSettings-Mobile-Toggle-${strings.Enable_Punch_Detail_Edit}`}
                    toggled={this.state.enableEmpEditTimecardOnMobile}
                    onToggle={this.handleToggleEnablePunchDetailEdit}
                    label={strings.Enable_Punch_Detail_Edit}
                    style={{ maxWidth: 300, marginBottom: 15 }}
                    disabled={this.props.readOnly}
                    info="Allows employees to edit punch details (such as hierarchy, position, equipment, job, phase) on timecard via mobile device"
                />
                <InfoIconToggle
                    data-intercom-target={`menu-AccountSettings-Mobile-Toggle-${strings.Missing_Punch_workflow}`}
                    toggled={this.state.missingPunchWorkflow}
                    onToggle={this.handleToggleMissingPunchWorkflow}
                    label={strings.Missing_Punch_workflow}
                    style={{ maxWidth: 300, marginBottom: 15 }}
                    disabled={this.props.readOnly}
                />
                <InfoIconToggle
                    data-intercom-target={`menu-AccountSettings-Mobile-Toggle-${strings.Limit_Employees_on_Device_to_Hierarchy}`}
                    toggled={this.state.limitDeviceEmployee}
                    onToggle={this.handleToggleLimitDeviceEmployee}
                    label={strings.Limit_Employees_on_Device_to_Hierarchy}
                    style={{ maxWidth: 300, marginBottom: 15 }}
                    disabled={this.props.readOnly}
                />
                <InfoIconToggle
                    data-intercom-target={`menu-AccountSettings-Mobile-Toggle-${strings.Control_biometric_settings_per_employee}`}
                    toggled={this.state.controlEmpBioSetting}
                    onToggle={this.handleToggleControlEmpBioSetting}
                    label={strings.Control_biometric_settings_per_employee}
                    style={{ maxWidth: 300, marginBottom: 15 }}
                    disabled={this.props.readOnly}
                />
                <InfoIconToggle
                    data-intercom-target={`menu-AccountSettings-Mobile-Toggle-Present_Timecard_on_Punch_Out}`}
                    toggled={this.state.presentTimecard}
                    onToggle={this.handleTogglePresentTimecard}
                    label={strings.Present_Timecard_on_Punch_Out}
                    style={{ maxWidth: 300, marginBottom: 15 }}
                    disabled={this.props.readOnly}
                />
                <InfoIconToggle
                    data-intercom-target={`menu-AccountSettings-Mobile-Toggle-ConfirmMobileNoPeriodically`}
                    toggled={this.state.confirmMobileNoPeriodically}
                    onToggle={this.handleToggleConfirmMobileNoPeriodically}
                    label={"Confirm Mobile Number Periodically"}
                    style={{ maxWidth: 300, marginBottom: 15 }}
                    disabled={this.props.readOnly}
                />
                <InfoIconToggle
                    data-intercom-target={`menu-AccountSettings-Mobile-Toggle-EnableLiveness`}
                    toggled={this.state.enableLiveness}
                    onToggle={this.handleToggleEnableLiveness}
                    label={"Enable Liveness"}
                    style={{ maxWidth: 300, marginBottom: 15 }}
                    disabled={this.props.readOnly}
                />
                
                <InfoIconToggle
                    data-intercom-target={`menu-AccountSettings-Features-Toggle-EnableGeoFenceTracking`}
                    toggled={this.state.enableGeoFenceTracking}
                    onToggle={this.handleToggleEnableGeoFenceTracking}
                    label={strings.Enable_GeoFence_Tracking}
                    style={{ maxWidth: 275, marginBottom: 15 }}
                    disabled={this.props.readOnly}
                    info="Control Geofence Boundary Notifications per Employee"
                />
                {/*<InfoIconToggle
                    toggled={this.state.enablePreQuestions}
                    onToggle={this.handleToggleEnablePreQuestions}
                    labelPosition="left"
                    label={strings.Require_Punch_Pre_Screen_Questions}
                    style={{ maxWidth: 300, marginTop: 15 }}
                /> */}
                {this.state.enablePreQuestions &&
                    <PunchPreQuestions />
                }
            </div>
        )
    }
}
